"use client";

import { useState } from "react";
import clsx from "clsx";

const proactiveActions = [
  {
    id: "rewrite",
    label: "Rewrite",
    eyebrow: "Page move ready",
    title: "I rewrote the hero around handoff risk.",
    body: "Approve it and Lucien applies the page change, watches conversion, and uses the result to choose the next move.",
    details: ["Uses 5 buyer replies", "Updates hero, subhead, and CTA"],
  },
  {
    id: "leads",
    label: "Find leads",
    eyebrow: "Buyer list ready",
    title: "I found 12 ops leads likely to feel the pain.",
    body: "Approve it and Lucien enriches the list, queues the right angle for each lead, and watches replies for urgency.",
    details: ["Matches the repeated pain", "Ranks by reachability and urgency"],
  },
  {
    id: "outreach",
    label: "Run outreach",
    eyebrow: "Sequence ready",
    title: "I drafted the first handoff-risk outreach sequence.",
    body: "Approve it and Lucien sends the test, handles follow-ups, and flags buyers who mention ownership, timing, or budget.",
    details: ["3-message sequence", "Follow-ups adapt to replies"],
  },
  {
    id: "market",
    label: "Watch market",
    eyebrow: "Signal found",
    title: "I found competitor and social posts pointing to a sharper angle.",
    body: "Lucien saw founders discussing post-sale handoff risk and incumbents shifting their copy. Approve the update and it refreshes the page message plus outreach angles.",
    details: ["Social posts + competitor pages", "Updates the buyer message map"],
  },
];

export function ProactiveActionTabs() {
  const [activeActionId, setActiveActionId] = useState(proactiveActions[0]?.id);
  const [previewAction, setPreviewAction] = useState<"review" | "approve" | null>(null);
  const activeAction =
    proactiveActions.find(action => action.id === activeActionId) ?? proactiveActions[0];

  return (
    <div className="mt-6">
      <div
        className="grid grid-cols-2 gap-2 sm:grid-cols-4"
        role="tablist"
        aria-label="Proactive actions drafted by Lucien"
      >
        {proactiveActions.map(action => {
          const isActive = action.id === activeAction.id;

          return (
            <button
              key={action.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`lucien-action-panel-${action.id}`}
              id={`lucien-action-tab-${action.id}`}
              onClick={() => {
                setActiveActionId(action.id);
                setPreviewAction(null);
              }}
              className={clsx(
                "inline-flex h-9 w-full items-center justify-center whitespace-nowrap rounded-full border text-center text-xs font-semibold transition-all duration-200 sm:text-sm",
                isActive
                  ? "border-blue/30 bg-blue text-white shadow-[0_10px_24px_rgba(0,71,249,0.18)]"
                  : "border-slate-200 bg-slate-50 text-slate-700 hover:border-blue/25 hover:bg-white hover:text-slate-950"
              )}
            >
              {action.label}
            </button>
          );
        })}
      </div>

      <div
        key={activeAction.id}
        id={`lucien-action-panel-${activeAction.id}`}
        role="tabpanel"
        aria-labelledby={`lucien-action-tab-${activeAction.id}`}
        className="mt-4 rounded-2xl border border-blue/15 bg-blue/[0.04] p-5"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs font-medium uppercase tracking-[0.14em] text-blue">
            {activeAction.eyebrow}
          </p>
          <span className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700 ring-1 ring-amber-200">
            Needs approval
          </span>
        </div>
        <h4 className="mt-3 text-lg font-semibold text-slate-950">{activeAction.title}</h4>
        <p className="mt-2 text-sm leading-6 text-slate-600">{activeAction.body}</p>

        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {activeAction.details.map(detail => (
            <div
              key={detail}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600"
            >
              {detail}
            </div>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setPreviewAction("review")}
            className="inline-flex h-9 items-center justify-center rounded-full border border-amber-300 bg-amber-50 px-4 text-xs font-semibold text-amber-700 hover:bg-amber-100"
          >
            Review
          </button>
          <button
            type="button"
            onClick={() => setPreviewAction("approve")}
            className="inline-flex h-9 items-center justify-center rounded-full bg-emerald-600 px-4 text-xs font-semibold text-white shadow-sm shadow-emerald-950/10 hover:brightness-95"
          >
            Approve
          </button>
        </div>
        {previewAction ? (
          <p className="mt-3 rounded-xl border border-blue/10 bg-white px-3 py-2 text-xs font-medium leading-5 text-slate-600">
            {previewAction === "approve"
              ? "Approved in this preview: Lucien would run this scoped action and watch the result."
              : "Review mode: Lucien would show the signal and exact changes before approval."}
          </p>
        ) : null}
      </div>
    </div>
  );
}
